<template>
  <div role="navigation" :aria-label="LANG.categoryTree">
    <div
      class="i-tree-item-row flex items-center"
      :class="{
        'selected': isAllSelected,
        'item-disabled': isAllSelectedDisabled,
      }"
      @click.prevent="() => selectTreeNode('')"
      @keydown.enter.prevent="() => selectTreeNode('')"
      tabindex="0"
    >
      <i-icon icon="search" :title="LANG.searchInTree" class="mh1 fa-fw" />
      <span class="sr-only">{{ LANG.searchInTree }} </span>
      <span>{{ isCategoryTree ? LANG.allCategories : LANG.allDirectories }}</span>
    </div>
    <i-tree-view
      v-if="treeStatus === Status.RESOLVED"
      :items="treeItems"
      :expandedKeys="['/', ...expandedNodes]"
      @update:expandedKeys="setExpandedNodes"
      :selectedKey="selectedTreeNode"
      @update:selectedKey="selectTreeNode"
      :expandable="item => item.has_children"
      :loadChildren="fetchDirectories"
      v-slot="{item, hover, selected, level, expandable, expandItem}"
    >
      <template v-if="isCategoryTree">
        <span class="i-tree-item-label flex-auto" :title="item.name || item.id">
          {{ item.name || item.id }}
        </span>
        <TreeNodeButtonMoreOptions
          v-if="hover || selected"
          :node="item"
          :expandable="expandable"
          :expandItem="expandItem"
        />
      </template>
      <template v-else>
        <span class="i-tree-item-label">
          {{ level === 0 ? item.id : item.id.split('/').pop() }}
        </span>
      </template>
    </i-tree-view>
    <div v-else-if="treeStatus === Status.LOADING" class="loader-wrapper">
      <Loading/>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import { mapBrowserState, mapBrowserActions, mapBrowserMutations, mapBrowserGetters } from '@/store'; // eslint-disable-line
import { TreeMode } from '@/store/modules/entity_browser/config';
import Loading from '@/components/common/Loading.vue';
import TreeNodeButtonMoreOptions from '@/components/browser/tree/TreeNodeButtonMoreOptions.vue';
import { Status } from '@/utils/useData';

export default defineComponent({
  components: {
    Loading,
    TreeNodeButtonMoreOptions,
  },
  props: {
    nodes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      Status,
    };
  },
  computed: {
    ...mapState(['LANG']),
    ...mapBrowserState(['treeMode']),
    ...mapBrowserGetters([
      'selectedTreeNode',
      'expandedNodes',
      'treeStatus',
      'isAllSelected',
      'isAllSelectedDisabled',
    ]),
    isCategoryTree() {
      return this.treeMode === TreeMode.CATEGORY;
    },
    treeItems() {
      const { nodes, treeMode } = this;
      if (treeMode === TreeMode.DIRECTORY) {
        return [{
          id: '/',
          children: nodes,
        }];
      }

      return nodes;
    },
  },
  methods: {
    ...mapBrowserActions([
      'fetchDirectories',
    ]),
    ...mapBrowserMutations([
      'selectTreeNode',
      'setExpandedNodes',
    ]),
  },
});
</script>

<style scoped lang="scss">
::v-deep {
  .i-tree-item-row {
    font-family: $font-family-regular;
    color: #191919;
    cursor: pointer;
    padding-left: 8px;
    height: 31px;
    outline-color: $orange;
    outline-width: 3px;
    outline-offset: -3px;
  }
  .i-tree-item-row:not(.selected):hover {
    background-color: #E8EBEF;
    color: $blue;
  }
  .i-tree-item-row.selected {
    background-color: $i-list-selected-bg;
    color: $blue;
  }
  .i-tree-item-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;
  }
}

.item-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.loader-wrapper {
  position: relative;
  margin: auto;

  // Experimental - Start
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  // Experimental - End
}
</style>
